import { useState } from 'react';
import { claveIdempotencia } from '../../../api/comun';
import type { AltaInvitacionRrpp, DatosTarifa, InvitacionRrpp } from '../../../api/ops/listas';
import { Boton } from '../../../componentes/Boton';
import { Campo } from '../../../componentes/Campo';
import { Desplegable } from '../../../componentes/Desplegable';
import { PanelLateral } from '../../../componentes/PanelLateral';
import { useAvisoBreve } from '../../../componentes/avisoBreve';
import { Bloque, Botones, Caja, Dato } from '../comun';
import { codigoDe, useAvisarError, useCarga } from '../uso';
import { aCentimos } from '../../../logica/dinero';
import { usePermisos } from '../../../permisos/permisos';
import { BASE, diaHora, opsDe, PERMISOS } from '../textos';
import type { ContextoRrpp } from './ApartadoRrpp';

/**
 * «Nuevo RRPP» (10.10, `invitePromoter`, G789: Administrador y Encargado): el email y, opcionales, nombre y móvil para
 * reconocer la invitación (G838); la comisión inicial (un importe fijo con su base) solo la ve quien tiene
 * `rrpp.tarifas.gestionar` (G857: el Encargado no). La invitación lleva el rol RRPP y caduca a los 7 días; al aceptar se crea
 * su ficha con su código de venta y su comisión inicial. Verla, reenviarla y anularla: «Invitaciones de RRPP» (G807).
 */
export function Invitar({ ctx }: { ctx: ContextoRrpp }) {
    const { props } = ctx;
    const { negocio, movil } = props;
    const ops = opsDe(props.ops);
    const avisar = useAvisoBreve();
    const avisarError = useAvisarError();
    const permisos = usePermisos();
    const conComision = permisos.puede(PERMISOS.tarifas);
    const [email, setEmail] = useState('');
    const [nombre, setNombre] = useState('');
    const [telefono, setTelefono] = useState('');
    const [base, setBase] = useState<DatosTarifa['basis']>('guest_arrival');
    const [importe, setImporte] = useState('');
    const [probado, setProbado] = useState(false);
    const [ocupado, setOcupado] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [clave, setClave] = useState(claveIdempotencia);
    const movilLimpio = telefono.replace(/[\s().-]/g, '');
    const okMovil = movilLimpio === '' || /^\+[1-9][0-9]{6,14}$/.test(movilLimpio);
    const centimos = importe.trim() === '' ? null : aCentimos(importe);
    const okImporte = importe.trim() === '' || (centimos !== null && centimos > 0);
    const okEmail = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());
    const ok = okEmail && okMovil && okImporte;
    const errEmail = probado && !okEmail ? (email.trim() === '' ? 'Escribe su email.' : 'Revisa el email.') : error;
    const alta = (): AltaInvitacionRrpp => ({
        email: email.trim().toLowerCase(),
        ...(nombre.trim() === '' ? {} : { display_name: nombre.trim() }),
        ...(movilLimpio === '' ? {} : { phone: movilLimpio }),
        ...(conComision && centimos !== null ? { initial_rate: { basis: base, amount: { amount: centimos, currency: ctx.rrpp[0]?.pending_total?.currency ?? 'EUR' } } } : {}),
    });

    const enviar = () => {
        if (!ok) {
            setProbado(true);
            return;
        }
        setOcupado(true);
        ops.invitePromoter(negocio, alta(), clave)
            .then((r) => {
                avisar({ tipo: 'ok', texto: `Invitación enviada a ${r.email}. Caduca el ${diaHora(r.expires_at)}.` });
                ctx.recargar();
                ctx.abrir(null);
            })
            .catch((e: unknown) => {
                const c = codigoDe(e);
                if (c === 'conflict' || c === 'already_member' || c === 'invitation_pending') {
                    setError('Ese email ya está en el equipo o tiene una invitación pendiente.');
                } else {
                    avisarError(e, 'No se ha podido enviar la invitación.');
                }
            })
            .finally(() => { setOcupado(false); });
    };

    return (
        <PanelLateral
            abierto
            onCerrar={() => { ctx.abrir(null); }}
            movil={movil}
            titulo="Nuevo RRPP"
            eyebrow="RRPP"
            subtitulo="Le invitamos por email; al aceptar entra en su portal."
            icono="user-plus"
            accion="Enviar invitación"
            accionIcono="send"
            ocupado={ocupado}
            ocupadoTexto="Enviando…"
            onAccion={enviar}
        >
            <div className="rv-ca-cuerpo">
                <Bloque titulo="Quién es">
                    <Campo etiqueta="Email" tipo="email" valor={email} onChange={(v) => { setEmail(v); setError(null); setClave(claveIdempotencia()); }} error={errEmail} ayuda="Le llega el enlace para unirse con el rol RRPP. Caduca a los 7 días." nombre="email-rrpp" onEnter={enviar} />
                    <Campo etiqueta="Nombre (opcional)" valor={nombre} onChange={(v) => { setNombre(v); setClave(claveIdempotencia()); }} ayuda="Para reconocer la invitación. Al aceptar, manda el nombre de su cuenta." max={120} />
                    <Campo etiqueta="Móvil (opcional)" tipo="tel" valor={telefono} onChange={(v) => { setTelefono(v); setClave(claveIdempotencia()); }} error={probado && !okMovil ? 'Escribe el móvil con el prefijo: +34…' : null} />
                </Bloque>
                {conComision && (
                    <Bloque titulo="Comisión inicial (opcional)">
                        <Desplegable etiqueta="Por cada" valor={base} onChange={(v) => { setBase(v as DatosTarifa['basis']); setClave(claveIdempotencia()); }} opciones={Object.entries(BASE).map(([k, [texto]]) => ({ valor: k, texto }))} />
                        <Campo etiqueta="Importe" valor={importe} onChange={(v) => { setImporte(v); setClave(claveIdempotencia()); }} error={probado && !okImporte ? 'Escribe un importe, por ejemplo 1,50.' : null} ayuda="Se crea como su tarifa al aceptar. Porcentaje o tramos, después en Comisiones." />
                    </Bloque>
                )}
                <Bloque>
                    <Dato icono="ticket" k="Código de venta" v="Se crea al aceptar" sub="Para su enlace de venta de entradas (?rrpp=…). Se puede regenerar." />
                    <Caja icono="smartphone">Al aceptar entra en su portal de RRPP: sus listas, sus enlaces y sus comisiones. Sus cupos en cada evento se ajustan en «En el evento».</Caja>
                </Bloque>
            </div>
        </PanelLateral>
    );
}

const ESTADO_INVITACION: Readonly<Record<NonNullable<InvitacionRrpp['status']>, string>> = { sent: 'Enviada', expired: 'Caducada', accepted: 'Aceptada', revoked: 'Anulada' };

/**
 * «Invitaciones de RRPP» (10.10, G807): las que llevan solo el rol RRPP, con el nombre y el móvil que se pusieron al invitar
 * (G838). Reenviar (enviada o caducada: enlace nuevo de 7 días) y Anular (enviada), con `rrpp.promotores.invitar`.
 */
export function InvitacionesRrpp({ ctx }: { ctx: ContextoRrpp }) {
    const { props } = ctx;
    const { negocio, soloLectura } = props;
    const ops = opsDe(props.ops);
    const avisar = useAvisoBreve();
    const avisarError = useAvisarError();
    const [vuelta, setVuelta] = useState(0);
    const [ocupada, setOcupada] = useState<string | null>(null);
    const lista = useCarga(() => ops.listPromoterInvitations(negocio, { limit: 50 }), [negocio, vuelta]);
    const abiertas = (lista.datos?.data ?? []).filter((i) => i.status === 'sent' || i.status === 'expired');
    if (abiertas.length === 0) {
        return null;
    }
    const hacer = (i: InvitacionRrpp, accion: 'reenviar' | 'anular') => {
        setOcupada(i.invitation_id);
        (accion === 'reenviar' ? ops.resendPromoterInvitation(negocio, i.invitation_id) : ops.revokePromoterInvitation(negocio, i.invitation_id))
            .then(() => {
                avisar({ tipo: 'ok', texto: accion === 'reenviar' ? `Invitación reenviada a ${i.email}.` : `Invitación a ${i.email} anulada.` });
                setVuelta((v) => v + 1);
            })
            .catch((e: unknown) => { avisarError(e, accion === 'reenviar' ? 'No se ha podido reenviar.' : 'No se ha podido anular.'); })
            .finally(() => { setOcupada(null); });
    };
    return (
        <Bloque titulo="Invitaciones de RRPP" intro="Las que aún no han aceptado.">
            {abiertas.map((i) => (
                <Dato key={i.invitation_id} icono="mail" k={i.display_name ?? i.email}
                    v={`${ESTADO_INVITACION[i.status ?? 'sent']}${i.status === 'sent' ? ` · caduca el ${diaHora(i.expires_at)}` : ''}`}
                    sub={[i.display_name == null ? '' : i.email, i.phone ?? '', i.has_initial_rate === true ? 'con comisión inicial' : ''].filter((x) => x !== '').join(' · ')}>
                    {!soloLectura && (
                        <Botones>
                            <Boton tipo="secundario" icono="send" ocupado={ocupada === i.invitation_id} onClick={() => { hacer(i, 'reenviar'); }}>Reenviar</Boton>
                            {i.status === 'sent' && <Boton tipo="fantasma" icono="x" onClick={() => { hacer(i, 'anular'); }}>Anular</Boton>}
                        </Botones>
                    )}
                </Dato>
            ))}
        </Bloque>
    );
}
